import { useQuery } from '@tanstack/react-query';
import { Building2, Eye, FolderOpen, Lock, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchEscritorios } from '@/api/client';
import { deleteCliente, fetchClientes, TIPO_PJ, updateCliente, type Cliente } from '@/api/clientes';
import { DataTablePagination } from '@/components/DataTablePagination';
import { SortableHeader } from '@/components/SortableHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OrigemBadge } from '@/components/ui/OrigemBadge';
import { SearchHighlight } from '@/components/ui/search-highlight';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TagsDoRegistro } from '@/components/tags/TagsDoRegistro';
import { TagChips } from '@/components/ui/tag-chip';
import { useTagsNoCache } from '@/hooks/useTagsNoCache';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { usePaginatedQuery } from '@/hooks/usePaginatedQuery';
import { useAtuacao } from '@/lib/delegacao/atuacao';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { formatDate, maskCpfPrivate } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import type { SearchStrictness } from '@/lib/search/smartSearch';

// --- Helpers ---

const statusConfig: Record<string, { label: string; className: string }> = {
  PROSPECTO: { label: 'Prospecto', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' },
  ATIVO: { label: 'Ativo', className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  INATIVO: { label: 'Inativo', className: 'bg-gray-100 text-gray-500 dark:bg-gray-800/40 dark:text-gray-400' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = statusConfig[status] ?? { label: status, className: '' };
  return <Badge variant="outline" className={cfg.className}>{cfg.label}</Badge>;
}

function formatCpfCnpj(cliente: Cliente): string {
  if (cliente.tipo_pessoa === TIPO_PJ) return cliente.cnpj ?? '—';
  return maskCpfPrivate(cliente.cpf);
}

// --- Props ---

interface ClienteTableProps {
  search?: string;
  /** Rigor da busca inteligente no servidor; só vale quando há `search`. */
  rigor?: SearchStrictness;
  statusFilter?: string;
  origemFilter?: string;
  /** Filtro por tags (PLANO_TAGS D7), aplicado no servidor. */
  tagIds?: string[];
  onEdit: (cliente: Cliente) => void;
}

// Lista paginada, detalhe e contagens do cliente: todos sob o prefixo `clientes`.
const CACHES_DE_CLIENTE = [queryKeys.clientes.all] as const;

export function ClienteTable({ search, rigor = 'flexible', statusFilter, origemFilter, tagIds, onEdit }: ClienteTableProps) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [deleteTarget, setDeleteTarget] = useState<Cliente | null>(null);
  const tagsNoCache = useTagsNoCache(CACHES_DE_CLIENTE);
  const [moverTarget, setMoverTarget] = useState<Cliente | null>(null);
  const [destino, setDestino] = useState('');
  const atuacao = useAtuacao();
  // A mesma consulta da visão do escritório (`['escritorios']`, persistida), sem depender do AuthProvider.
  const { data: escritorios } = useQuery({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });
  // O cliente do escritório só o gestor dele exclui (PLANO_CLIENTE_DO_ESCRITORIO R3), como o
  // servidor: o "Excluir" para todo membro oferecia uma ação que respondia 403.
  const podeExcluir = (cliente: Cliente) => {
    if (!cliente.escritorio_id) return true;
    const escritorio = escritorios?.find((e) => e.id === cliente.escritorio_id);
    return !!escritorio && (!!escritorio.sou_gestor || escritorio.meu_role === 'PROPRIETARIO');
  };

  const {
    data,
    isLoading,
    error,
    pagination,
    sorting,
  } = usePaginatedQuery<Cliente>({
    queryKey: [
      ...queryKeys.clientes.all,
      { search, rigor: search ? rigor : undefined, status: statusFilter, origem: origemFilter, tagIds },
    ],
    fetchFn: (params) =>
      fetchClientes({
        ...params,
        search: search || undefined,
        rigor: search ? rigor : undefined,
        status: statusFilter || undefined,
        origem: origemFilter || undefined,
        tag_ids: tagIds,
      }),
    defaultSort: { orderBy: 'created_at', orderDir: 'desc' },
  });

  // Filtro novo (busca, rigor, aba, origem, tags) volta à página 1: na página 3 de um
  // filtro que cabe numa só, a lista viria vazia — e o estado vazio esconde a paginação.
  const filtros = JSON.stringify([search, search ? rigor : undefined, statusFilter, origemFilter, tagIds ?? []]);
  const [filtrosAnteriores, setFiltrosAnteriores] = useState(filtros);
  if (filtros !== filtrosAnteriores) {
    setFiltrosAnteriores(filtros);
    pagination.resetPage();
  }

  const moverMutation = useOfflineMutation<unknown, { id: string; escritorio_id: string }>({
    mutationFn: ({ id, escritorio_id }) => updateCliente(id, { escritorio_id }),
    outbox: {
      type: 'cliente_editar',
      getPayload: ({ id, escritorio_id }) => ({ method: 'PUT', url: `/clientes/${id}`, body: { escritorio_id } }),
      getDescription: () => `Mover ${moverTarget?.nome ?? 'cliente'} para o escritório`,
    },
    invalidateKeys: [
      [...queryKeys.clientes.all],
      [...queryKeys.clientes.counts],
    ],
    onSuccess: () => {
      showSuccess('Cliente movido para o escritório');
      setMoverTarget(null);
    },
    onQueued: () => setMoverTarget(null),
    onError: (err) => notifyMutationError(err, showError),
  });

  const deleteMutation = useOfflineMutation<void, string>({
    mutationFn: (id) => deleteCliente(id),
    outbox: {
      type: 'cliente_deletar',
      getPayload: (id) => ({ method: 'DELETE', url: `/clientes/${id}` }),
      getDescription: (id) => `Excluir cliente ${id}`,
    },
    invalidateKeys: [
      [...queryKeys.clientes.all],
      [...queryKeys.clientes.counts],
    ],
    onSuccess: () => {
      showSuccess('Cliente excluído com sucesso');
      setDeleteTarget(null);
    },
    onQueued: () => setDeleteTarget(null),
    onError: (err) => notifyMutationError(err, showError),
  });

  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size={32} label="Carregando clientes" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{classifyError(error).message}</AlertDescription>
      </Alert>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {search || tagIds ? 'Nenhum cliente encontrado com estes filtros.' : 'Nenhum cliente cadastrado.'}
      </div>
    );
  }

  return (
    <>
      {/* Tabela */}
      <div className="rounded-md border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                field="nome"
                label="Nome"
                currentField={sorting.orderBy}
                currentDir={sorting.orderDir}
                onToggle={sorting.toggleSort}
              />
              <TableHead className="hidden sm:table-cell">CPF/CNPJ</TableHead>
              <TableHead className="hidden md:table-cell">Telefone</TableHead>
              <SortableHeader
                field="status"
                label="Status"
                currentField={sorting.orderBy}
                currentDir={sorting.orderDir}
                onToggle={sorting.toggleSort}
              />
              <TableHead className="hidden lg:table-cell">Escritório</TableHead>
              <SortableHeader
                field="created_at"
                label="Criado em"
                currentField={sorting.orderBy}
                currentDir={sorting.orderDir}
                onToggle={sorting.toggleSort}
                className="hidden sm:table-cell"
              />
              <TableHead>Tags</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((cliente) => (
              <TableRow
                key={cliente.id}
                className="cursor-pointer hover:bg-accent"
                onClick={() => navigate(`/app/clientes/${cliente.id}`)}
              >
                <TableCell className="font-medium">
                  <SearchHighlight text={cliente.nome} query={search} />
                </TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground">
                  {formatCpfCnpj(cliente)}
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">
                  {cliente.telefone ?? '—'}
                </TableCell>
                <TableCell>
                  <StatusBadge status={cliente.status} />
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  {/* No escritório o cliente é da casa: o pessoal virou a exceção, e aparece. */}
                  {cliente.origem === 'pessoal' && (escritorios?.length ?? 0) > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      <Lock className="h-2.5 w-2.5" />
                      Só eu
                    </span>
                  ) : (
                    <OrigemBadge item={cliente} />
                  )}
                </TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground">
                  {formatDate(cliente.created_at)}
                </TableCell>
                <TableCell>
                  {cliente.origem === 'compartilhado' ? (
                    <TagChips tags={cliente.tags} max={2} />
                  ) : (
                    <TagsDoRegistro
                      recurso="clientes"
                      registroId={cliente.id}
                      identificador={cliente.nome}
                      tags={cliente.tags}
                      noCache={tagsNoCache}
                      max={2}
                      compacto
                    />
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/app/clientes/${cliente.id}`); }}>
                        <Eye className="mr-2 h-4 w-4" />
                        Ver
                      </DropdownMenuItem>
                      {/* Cliente de colega (visão do escritório): só para ver. */}
                      {cliente.origem !== 'compartilhado' && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(cliente); }}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/app/clientes/${cliente.id}?tab=documentos`); }}>
                        <FolderOpen className="mr-2 h-4 w-4" />
                        Documentos
                      </DropdownMenuItem>
                      {/* O cliente é da casa (PLANO_CLIENTE_DO_ESCRITORIO): o pessoal se MOVE para
                          o escritório, não se compartilha. Na conta de outra pessoa, não (é do titular). */}
                      {cliente.origem === 'pessoal' && !atuacao && (escritorios?.length ?? 0) > 0 && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setDestino(escritorios?.length === 1 ? escritorios[0].id : '');
                            setMoverTarget(cliente);
                          }}
                        >
                          <Building2 className="mr-2 h-4 w-4" />
                          Mover para o escritório…
                        </DropdownMenuItem>
                      )}
                      {cliente.origem !== 'compartilhado' && podeExcluir(cliente) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(cliente); }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Excluir
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginacao */}
      <DataTablePagination
        page={pagination.page}
        pageSize={pagination.pageSize}
        total={pagination.total}
        pages={pagination.pages}
        onPageChange={pagination.goToPage}
        onPageSizeChange={pagination.changePageSize}
      />

      {/* Mover o cliente pessoal para o escritório (R3: qualquer membro; voltar é do gestor) */}
      <Dialog open={!!moverTarget} onOpenChange={(open) => !open && setMoverTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mover para o escritório</DialogTitle>
            <DialogDescription>
              <strong>{moverTarget?.nome}</strong> passa a ser cliente do escritório: todos os membros o veem, com os
              documentos dele. Tirá-lo do escritório depois é só do gestor.
            </DialogDescription>
          </DialogHeader>
          {(escritorios?.length ?? 0) > 1 && (
            <Select value={destino} onValueChange={setDestino}>
              <SelectTrigger aria-label="Escritório">
                <SelectValue placeholder="Escolha o escritório" />
              </SelectTrigger>
              <SelectContent>
                {escritorios?.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              disabled={!destino || moverMutation.isPending}
              onClick={() => moverTarget && moverMutation.mutate({ id: moverTarget.id, escritorio_id: destino })}
            >
              {moverMutation.isPending ? <Spinner className="mr-2 text-inherit" /> : null}
              Mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmacao de exclusao */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir cliente</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir <strong>{deleteTarget?.nome}</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? <Spinner className="mr-2 text-inherit" /> : null}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
